import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import type { UserPublic, UserRole, UserStatus, UserTier } from '@xdocs/schemas';
import { Badge, DataTable, Select, cn } from '@xdocs/ui';
import type { BadgeTone, ColumnDef } from '@xdocs/ui';
import { RoleGate, useAdminT, useAdminUsersList } from '../../../features/admin/index.js';
import { optionalProp } from '../../../lib/optionalProp.js';
import styles from './UsersListPage.module.css';

const ROLE_TONE: Record<UserRole, BadgeTone> = { admin: 'error', moderator: 'info', user: 'neutral' };
const TIER_TONE: Record<UserTier, BadgeTone> = { pro: 'pro', free: 'neutral' };
const STATUS_TONE: Record<UserStatus, BadgeTone> = { active: 'success', suspended: 'error', deleted: 'neutral' };

const ROLES: readonly UserRole[] = ['user', 'moderator', 'admin'];
const TIERS: readonly UserTier[] = ['free', 'pro'];
const STATUSES: readonly UserStatus[] = ['active', 'suspended', 'deleted'];

/** `/admin/users` (F54, design/app/39-admin-users.html): server-side
 * paginated/searched/filtered `<DataTable>` of every platform user. Row
 * click routes to `/admin/users/:id`. */
export function UsersListPage(): ReactElement {
  return (
    <RoleGate minimum="admin">
      <UsersListContent />
    </RoleGate>
  );
}

function UsersListContent(): ReactElement {
  const { t } = useAdminT();
  const navigate = useNavigate();
  const {
    rows,
    total,
    nextCursor,
    query,
    filters,
    onQueryChange,
    onFiltersChange,
    onLoadMore,
    isLoading,
    isLoadingMore,
    isError,
    loadMoreError,
    onRetry,
  } = useAdminUsersList();

  const columns: ColumnDef<UserPublic>[] = [
    {
      id: 'email',
      header: t('admin:users.column.email'),
      primary: true,
      cell: (row) => (
        <span className={cn(styles['emailCell'])}>
          <span className={cn(styles['email'])}>{row.email}</span>
          {/* mockup-39: missing display name renders as an em dash, never a blank cell (39-admin-users.html spam@throwaway.io row) */}
          <span className={cn(styles['name'])}>{row.displayName ?? '—'}</span>
        </span>
      ),
    },
    {
      id: 'role',
      header: t('admin:users.column.role'),
      sortable: true,
      cell: (row) => <Badge tone={ROLE_TONE[row.role]}>{t(`admin:role.${row.role}`)}</Badge>,
    },
    {
      id: 'tier',
      header: t('admin:users.column.tier'),
      sortable: true,
      cell: (row) => <Badge tone={TIER_TONE[row.tier]}>{t(`admin:tier.${row.tier}`)}</Badge>,
    },
    {
      id: 'credits',
      header: t('admin:users.column.credits'),
      hideOnMobile: true,
      cell: (row) => <span className={cn(styles['mono'])}>{row.creditsBalance}</span>,
    },
    {
      id: 'status',
      header: t('admin:users.column.status'),
      sortable: true,
      cell: (row) => <Badge tone={STATUS_TONE[row.status]}>{t(`admin:status.${row.status}`)}</Badge>,
    },
  ];

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <h1 className={cn(styles['title'])}>{t('admin:users.title')}</h1>
        {total !== null && <span className={cn(styles['count'])}>{t('admin:users.count', { count: total })}</span>}
      </div>

      <div className={cn(styles['filters'])}>
        <Select
          label={t('admin:users.filter.role')}
          value={filters.role ?? ''}
          onChange={(event) => onFiltersChange({ ...filters, ...optionalProp('role', (event.target.value || undefined) as UserRole | undefined) })}
          options={[{ value: '', label: t('admin:users.filter.roleAll') }, ...ROLES.map((role) => ({ value: role, label: t(`admin:role.${role}`) }))]}
        />
        <Select
          label={t('admin:users.filter.tier')}
          value={filters.tier ?? ''}
          onChange={(event) => onFiltersChange({ ...filters, ...optionalProp('tier', (event.target.value || undefined) as UserTier | undefined) })}
          options={[{ value: '', label: t('admin:users.filter.tierAll') }, ...TIERS.map((tier) => ({ value: tier, label: t(`admin:tier.${tier}`) }))]}
        />
        <Select
          label={t('admin:users.filter.status')}
          value={filters.status ?? ''}
          onChange={(event) => onFiltersChange({ ...filters, ...optionalProp('status', (event.target.value || undefined) as UserStatus | undefined) })}
          options={[{ value: '', label: t('admin:users.filter.statusAll') }, ...STATUSES.map((status) => ({ value: status, label: t(`admin:status.${status}`) }))]}
        />
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        query={query}
        onQueryChange={onQueryChange}
        label={t('admin:users.title')}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        total={total}
        nextCursor={nextCursor}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
        {...optionalProp('loadMoreError', loadMoreError)}
        onRowClick={(row) => void navigate(`/admin/users/${row.id}`)}
        emptyTitle={t('admin:users.empty.title')}
        emptyDescription={t('admin:users.empty.description')}
        noResultsTitle={t('admin:users.noResults.title', { query: query.q ?? '' })}
        onClearFilters={() => {
          const { q: _q, ...rest } = query;
          onQueryChange({ ...rest, page: 1 });
          onFiltersChange({});
        }}
        searchLabel={t('admin:users.search.label')}
        searchPlaceholder={t('admin:users.search.placeholder')}
        searchAlign="start"
      />
    </div>
  );
}
